import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AdmissionApplication, AdmissionEnquiry, AdmissionInterviewSlot } from '../models/admissions.model';

const LS_ENQ = 'eschool-admissions-enquiries-v1';
const LS_APP = 'eschool-admissions-applications-v1';
const LS_INT = 'eschool-admissions-interviews-v1';

@Injectable({
  providedIn: 'root'
})
export class AdmissionsService {

  private readonly base = `${environment.apiUrl}/api/v1/admissions`;

  constructor(private http: HttpClient) { }

  listEnquiries(): Observable<AdmissionEnquiry[]> {
    return this.http.get<AdmissionEnquiry[]>(`${this.base}/enquiries`).pipe(
      tap(list => this.cache(LS_ENQ, list)),
      catchError(() => of(this.readLocal<AdmissionEnquiry[]>(LS_ENQ, [])))
    );
  }

  createEnquiry(body: {
    childFirstName: string;
    childLastName: string;
    seekingClass: string;
    academicYear: string;
    parentName: string;
    parentPhone: string;
    parentEmail?: string;
    notes?: string;
  }): Observable<AdmissionEnquiry> {
    const row: AdmissionEnquiry = {
      ...body,
      id: 'enq-' + Date.now(),
      createdAt: new Date().toISOString(),
      stage: 'ENQUIRY'
    };
    return this.http.post<AdmissionEnquiry>(`${this.base}/enquiries`, row).pipe(
      catchError(() => {
        const cur = this.readLocal<AdmissionEnquiry[]>(LS_ENQ, []);
        const next = [row, ...cur];
        window.localStorage.setItem(LS_ENQ, JSON.stringify(next));
        return of(row);
      })
    );
  }

  listApplications(): Observable<AdmissionApplication[]> {
    return this.http.get<AdmissionApplication[]>(`${this.base}/applications`).pipe(
      tap(list => this.cache(LS_APP, list)),
      catchError(() => of(this.readLocal<AdmissionApplication[]>(LS_APP, [])))
    );
  }

  listInterviews(): Observable<AdmissionInterviewSlot[]> {
    return this.http.get<AdmissionInterviewSlot[]>(`${this.base}/interviews`).pipe(
      tap(list => this.cache(LS_INT, list)),
      catchError(() => of(this.readLocal<AdmissionInterviewSlot[]>(LS_INT, [])))
    );
  }

  updateEnquiryStage(id: string, stage: AdmissionEnquiry['stage']): Observable<void> {
    return this.http.patch<void>(`${this.base}/enquiries/${encodeURIComponent(id)}`, { stage }).pipe(
      catchError(() => {
        const cur = this.readLocal<AdmissionEnquiry[]>(LS_ENQ, []);
        const next = cur.map(e => (e.id === id ? { ...e, stage } : e));
        window.localStorage.setItem(LS_ENQ, JSON.stringify(next));
        return of(undefined);
      })
    );
  }

  private cache(key: string, list: unknown[]): void {
    try {
      window.localStorage.setItem(key, JSON.stringify(list));
    } catch { /* ignore */ }
  }

  private readLocal<T>(key: string, fallback: T): T {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return fallback;
    }
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  }
}
